import { RefObject, useMemo } from "react";
import {
  A11Y_CHILD_MIN_INDEX,
  A11Y_DEFAULT_CURRENT_INDEX,
  useA11yNestedNavigation,
} from "../../a11y";
import { DropdownProps } from "../types";

export const useDropdownA11y = ({
  items,
  children,
  dropdownRef,
  onOutsideClick,
}: Pick<DropdownProps, "items" | "children" | "onOutsideClick"> & {
  dropdownRef: RefObject<HTMLUListElement | null>;
}) => {
  const enabledItemsData = useMemo(() => {
    const itemIndexes = [];
    let maxIndex = A11Y_CHILD_MIN_INDEX;

    for (let index = 0; items.length > index; index++) {
      if (items[index].disabled) {
        itemIndexes.push(-2);
      } else {
        itemIndexes.push(maxIndex);

        if (items.length >= index + 1) {
          maxIndex++;
        }
      }
    }

    return { itemIndexes, maxIndex: maxIndex - 1 };
  }, [items]);

  const scrollTop = () => dropdownRef.current?.scrollTo({ top: 0 });

  const { handleAfterBlur, handleEnter, a11yCurrentChildIndex, ...rest } =
    useA11yNestedNavigation({
      maxIndex: enabledItemsData.maxIndex,
      hasFocusableChildren: true,
    });

  const resetRootFocus = () => {
    handleAfterBlur();
    scrollTop();
  };

  const handleA11yClose = () => {
    if (a11yCurrentChildIndex === A11Y_DEFAULT_CURRENT_INDEX) {
      return;
    }

    const { onClick } = children.props;

    if (onClick) {
      onClick(new Event("click"));
    } else if (onOutsideClick) {
      onOutsideClick(new Event("click"));
    }

    resetRootFocus();
  };

  const handleEnterKeyDown = () => {
    scrollTop();
    handleEnter();
  };

  return {
    a11yCurrentChildIndex,
    enabledItemsData,
    resetRootFocus,
    handleA11yClose,
    handleEnterKeyDown,
    ...rest,
  };
};
